import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { InvoiceWithItemsRequest, Invoice, LineItem, default_payment_instruction } from './invoiceType';
import { EstimateWithItemizedEstimate, ItemizedEstimate } from '../estimate/estimateType'
import { ProjectAndCustomer } from '../project/projectType';
import ProjectApi from '../project/projectApi';
import InvoiceApi from './invoiceApi';
import HorizontalMenu from '../components/common/horizontalMenu';
import Breadcrumbs from '../components/common/breadcrumbs';
import { Alert } from '../components/ui/alert';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import Spinner from '../components/ui/spinner';
import { FilePlus, Download } from 'lucide-react';
import { formatAddress, formatCurrency } from '../lib/global_function';
import CreateItemizedInvoice from './createItemizedInvoice';
import EstimateApi from '../estimate/estimateApi';

const CreateInvoicePage = () => {
  const breadcrumbs = [
    { label: 'Project', path: '/main/project' },
    { label: 'Invoice', path: '/main/project/invoice' },
    { label: 'Create', path: '/main/project/invoice/create' },
  ];

  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [estimateNotFound, setEstimateNotFound] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  const [selectedProjectId, setSelectedProjectId] = useState<string | null>(null);
  const [selectedCustomer, setSelectedCustomer] = useState<ProjectAndCustomer | null>(null);
  const [projectsAndCustomers, setProjectsAndCustomers] = useState<ProjectAndCustomer[]>([]);
  const [estimatesAndItemizedEstimate, setEstimatesAndItemizedEstimate] = useState<EstimateWithItemizedEstimate[]>([]);

  const [invoice, setInvoice] = useState({
    name: '',
    payment_instruction: default_payment_instruction,
    tax_rate: 0,
    discount_usd: 0,
    total_dollar: 0,
    tax_amount: 0,
    final_total: 0,
    issue_date: new Date().toISOString().split('T')[0],
    due_date: new Date(Date.now() + 14 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
  });

  const [lineItems, setLineItems] = useState<LineItem[]>([
    {
      id: Date.now(),
      code: '',
      description: '',
      amount_dollar: 0,
      invoice_id: '',
    },
  ]);

  // Calculate totals whenever relevant values change
  const calculateTotals = (subtotal: number, taxRate: number, discountUsd: number) => {
    const taxAmount = (subtotal * taxRate) / 100;
    const totalAmount = subtotal + taxAmount - discountUsd;

    return {
      tax_amount: taxAmount,
      final_total: totalAmount,
    };
  };

  // Update invoice totals when line items or other values change
  useEffect(() => {
    const subtotal = lineItems.reduce((sum, item) => sum + (item.amount_dollar || 0), 0);
    const { tax_amount, final_total } = calculateTotals(
      subtotal,
      invoice.tax_rate,
      invoice.discount_usd
    );

    setInvoice(prev => ({
      ...prev,
      total_dollar: subtotal,
      tax_amount,
      final_total
    }));
  }, [lineItems, invoice.tax_rate, invoice.discount_usd]);

  useEffect(() => {
    const fetchProjectsAndCustomers = async () => {
      try {
        const fetchedProjectsAndCustomers = await ProjectApi.getProjectsAndCustomers();
        setProjectsAndCustomers(fetchedProjectsAndCustomers);

        const projectFromState = location.state?.projectAndCustomer;
        if (projectFromState) {
          setSelectedProjectId(projectFromState.project_id.toString());
          setSelectedCustomer(projectFromState);
        }
      } catch (error) {
        setErrorMessage(error instanceof Error ? error.message : 'An unknown error occurred.');
      }
    };
    fetchProjectsAndCustomers();
  }, [location.state]);

  const handleProjectChange = (projectId: string) => {
    setSelectedProjectId(projectId);
    const selected = projectsAndCustomers.find(
      (projCust) => projCust.project_id.toString() === projectId.toString()
    );
    setSelectedCustomer(selected || null);
    setEstimateNotFound(false); // Reset estimate not found state
  };

  const handleTotalsChange = (totals: { dollars: number }) => {
    // This will trigger the useEffect above to recalculate totals
    // The subtotal is already calculated in the useEffect from lineItems
  };

  const handleTaxRateChange = (value: string) => {
    const inputValue = parseFloat(value) || 0;
    const cappedValue = Math.min(inputValue, 20);
    setInvoice(prev => ({ ...prev, tax_rate: cappedValue }));
  };

  const handleDiscountChange = (value: string) => {
    const inputValue = parseFloat(value) || 0;
    const cappedValue = Math.min(inputValue, 99999);
    setInvoice(prev => ({ ...prev, discount_usd: cappedValue }));
  };

  const resetForm = () => {
    setInvoice({
      name: '',
      payment_instruction: default_payment_instruction,
      tax_rate: 0,
      discount_usd: 0,
      total_dollar: 0,
      tax_amount: 0,
      final_total: 0,
      issue_date: new Date().toISOString().split('T')[0],
      due_date: new Date(Date.now() + 14 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    });
    setLineItems([
      {
        id: Date.now(),
        code: '',
        description: '',
        amount_dollar: 0,
        invoice_id: '',
      },
    ]);
    setMessage(null);
    setErrorMessage(null);
    setEstimateNotFound(false);
    setSelectedProjectId(null);
    setSelectedCustomer(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrorMessage('');
    setMessage('');
    setIsSubmitting(true);

    if (!selectedProjectId || !selectedCustomer) {
      setErrorMessage('Please select a project.');
      setIsSubmitting(false);
      return;
    }

    const subtotal = lineItems.reduce((sum, item) => sum + item.amount_dollar, 0);
    if (subtotal === 0) {
      setErrorMessage('Enter amount in dollars.');
      setIsSubmitting(false);
      return;
    }

    try {
      // Prepare itemized invoices data
      const itemizedInvoices = lineItems.map((item) => ({
        code: item.code,
        description: item.description,
        amount_dollar: item.amount_dollar,
      }));

      // Prepare the complete invoice payload (flat structure matching InvoiceWithItemsRequest)
      const invoicePayload: InvoiceWithItemsRequest = {
        name: invoice.name,
        payment_instruction: invoice.payment_instruction,
        amount_paid: 0,
        tax_rate: invoice.tax_rate,
        discount_usd: invoice.discount_usd,
        issue_date: invoice.issue_date,
        due_date: invoice.due_date,
        payment_date: '',
        payment_method: '',
        note: '',
        project_id: selectedProjectId,
        customer_id: selectedCustomer.customer_id,
        itemized_invoices: itemizedInvoices
      };

      const invoiceResponse = await InvoiceApi.createInvoice(invoicePayload);
      setMessage('Invoice and itemized invoices created successfully');
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (error) {
      setErrorMessage(error instanceof Error ? error.message : 'An unknown error occurred.');
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClickForGettingEstimate = async () => {
    if (!selectedCustomer?.customer_id) {
      setErrorMessage('No customer found.');
      return;
    }

    try {
      setEstimateNotFound(false);
      const estimatesAndItemizedEstimate = await EstimateApi.getEstimatesByCustomer(selectedCustomer.customer_id, selectedProjectId || '');
      setEstimatesAndItemizedEstimate(estimatesAndItemizedEstimate);

      if (estimatesAndItemizedEstimate.length > 0) {
        const firstEstimate = estimatesAndItemizedEstimate[0];

        // Populate invoice name and other fields - ensure numeric values are converted to numbers
        setInvoice(prev => ({
          ...prev,
          name: firstEstimate.name,
          tax_rate: Number(firstEstimate.tax_rate) || 0,
          discount_usd: Number(firstEstimate.discount_usd) || 0,
        }));

        // Populate line items with itemizedEstimates
        const newLineItems = firstEstimate.itemized_estimates.map((itemizedEstimate: ItemizedEstimate) => ({
          id: Date.now() + Math.random(), // Generate a unique ID for each line item
          code: itemizedEstimate.code,
          description: itemizedEstimate.description,
          amount_dollar: Number(itemizedEstimate.sub_total_dollar),
          invoice_id: '',
        }));

        setLineItems(newLineItems);
      } else {
        // No estimate found - clear/refresh all fields
        setEstimateNotFound(true);
        setInvoice(prev => ({
          ...prev,
          name: '',
          tax_rate: 0,
          discount_usd: 0,
        }));
        setLineItems([
          {
            id: Date.now(),
            code: '',
            description: '',
            amount_dollar: 0,
            invoice_id: '',
          },
        ]);
      }
    } catch (error) {
      // Check if it's a 404 error
      if (error instanceof Error && error.message.includes('404')) {
        setEstimateNotFound(true);
        // Clear/refresh all fields on error
        setInvoice(prev => ({
          ...prev,
          name: '',
          tax_rate: 0,
          discount_usd: 0,
        }));
        setLineItems([
          {
            id: Date.now(),
            code: '',
            description: '',
            amount_dollar: 0,
            invoice_id: '',
          },
        ]);
      } else {
        setErrorMessage(error instanceof Error ? error.message : 'An unknown error occurred.');
      }
    }
  };

  const taxAmount = Number(invoice.tax_amount) || 0;
  const discountAmount = Number(invoice.discount_usd) || 0;

  return (
    <>
      <HorizontalMenu />
      <div className="container-safe py-6">
        <Breadcrumbs items={breadcrumbs} />

        <div className="mt-6">
          <h1 className="text-2xl font-semibold text-foreground mb-6">Create Invoice</h1>

          {/* Error Message */}
          {errorMessage && (
            <Alert variant="error" className="mb-6">
              {errorMessage}
            </Alert>
          )}

          {/* Success Message with Actions */}
          {message && (
            <div className="mb-6 space-y-4">
              <Alert variant="success">
                {message}
              </Alert>
              <div className="flex flex-wrap gap-3">
                <Button size="medium" onClick={() => navigate('/main/project/invoice')}>
                  View All Invoices
                </Button>
                <Button size="medium" variant="inverted" onClick={resetForm}>
                  Create Another Invoice
                </Button>
              </div>
            </div>
          )}

          {/* Show form even after success, but make it collapsible or hidden */}
          <div className={message ? 'hidden' : ''}>
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Main Form Section - 2 columns */}
                <div className="lg:col-span-2 space-y-6">
                  {/* Project Selection & Customer Info - Compact */}
                  <div className="p-6 bg-white border border-gray-200 rounded-lg space-y-4">
                    {/* Project Dropdown */}
                    <div>
                      <Label htmlFor="selectedProject">
                        Select Project <span className="text-destructive">*</span>
                      </Label>
                      <Select
                        id="selectedProject"
                        name="selectedProject"
                        value={selectedProjectId || ''}
                        onChange={(e) => handleProjectChange(e.target.value)}
                        required
                      >
                        <option value="">Select Project</option>
                        {projectsAndCustomers.map((projCust) => (
                          <option key={projCust.project_id} value={projCust.project_id}>
                            {projCust.name}
                            {projCust.project_description
                              ? ` - ${projCust.project_description.substring(0, 100)}`
                              : ''}
                          </option>
                        ))}
                      </Select>
                    </div>

                    {/* Customer Information & Populate Button - Only show after project selected */}
                    {selectedCustomer && (
                      <div className="pt-4 border-t border-gray-200">
                        <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
                          {/* Customer Info */}
                          <div className="flex-1">
                            <p className="text-xs font-semibold text-gray-600 mb-1">CUSTOMER</p>
                            <p className="text-sm text-foreground">
                              <strong>{selectedCustomer.customer_first_name} {selectedCustomer.customer_last_name}</strong>
                            </p>
                            <p className="text-sm text-gray-700">
                              {formatAddress(
                                selectedCustomer.customer_address_1,
                                '',
                                selectedCustomer.customer_city,
                                selectedCustomer.customer_state,
                                selectedCustomer.customer_zip
                              )}
                            </p>
                            <p className="text-sm text-gray-700">
                              {selectedCustomer.customer_email} • {selectedCustomer.customer_phone}
                            </p>
                          </div>

                          {/* Populate from Estimate Button */}
                          <div className="flex-shrink-0">
                            <Button
                              type="button"
                              size="medium"
                              variant="inverted"
                              onClick={handleClickForGettingEstimate}
                              className="w-full lg:w-auto"
                            >
                              <Download size={18} />
                              Populate from Lastest Estimate
                            </Button>
                          </div>
                        </div>

                        {/* Estimate Not Found Message */}
                        {estimateNotFound && (
                          <Alert variant="info" className="mt-3">
                            No estimate found for this customer. You'll need to create the invoice from scratch.
                          </Alert>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Invoice Details */}
                  <div className="p-6 bg-white border border-gray-200 rounded-lg space-y-4">
                    <h2 className="text-lg font-semibold text-foreground mb-4">Invoice Details</h2>

                    {/* Invoice Name */}
                    <div>
                      <Label htmlFor="invoiceName">
                        Invoice Name <span className="text-destructive">*</span>
                      </Label>
                      <Input
                        id="invoiceName"
                        value={invoice.name}
                        placeholder="Enter Invoice Name"
                        onChange={(e) => setInvoice({ ...invoice, name: e.target.value })}
                        required
                      />
                    </div>

                    {/* Payment Instruction */}
                    <div>
                      <Label htmlFor="paymentInstruction">
                        Payment Instruction
                      </Label>
                      <p className="text-xs text-gray-600 mb-2">
                        This template will be displayed in the invoice sent to the customer. Update it with your own payment instruction.
                      </p>
                      <Textarea
                        id="paymentInstruction"
                        value={invoice.payment_instruction}
                        onChange={(e) => setInvoice({ ...invoice, payment_instruction: e.target.value })}
                        rows={4}
                        className="min-h-[100px]"
                      />
                    </div>

                    {/* Issue Date and Due Date */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="issue_date">Issue Date</Label>
                        <Input
                          id="issue_date"
                          type="date"
                          value={invoice.issue_date}
                          onChange={(e) => setInvoice({ ...invoice, issue_date: e.target.value })}
                        />
                      </div>
                      <div>
                        <Label htmlFor="due_date">Due Date</Label>
                        <Input
                          id="due_date"
                          type="date"
                          value={invoice.due_date}
                          onChange={(e) => setInvoice({ ...invoice, due_date: e.target.value })}
                        />
                      </div>
                    </div>

                    {/* Tax Rate and Discount */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="taxRate">Tax Rate (%)</Label>
                        <Input
                          id="taxRate"
                          type="number"
                          min={0}
                          max={20}
                          step="0.1"
                          placeholder="0"
                          value={invoice.tax_rate === 0 ? '' : invoice.tax_rate}
                          onChange={(e) => handleTaxRateChange(e.target.value)}
                        />
                      </div>
                      <div>
                        <Label htmlFor="discount">Discount ($)</Label>
                        <Input
                          id="discount"
                          type="number"
                          min={0}
                          max={99999}
                          step="10"
                          placeholder="0"
                          value={invoice.discount_usd === 0 ? '' : invoice.discount_usd}
                          onChange={(e) => handleDiscountChange(e.target.value)}
                        />
                      </div>
                    </div>
                  </div>

                  {/* Line Items */}
                  <div className="p-6 bg-white border border-gray-200 rounded-lg">
                    <h2 className="text-lg font-semibold text-foreground mb-4">Line Items</h2>
                    <CreateItemizedInvoice
                      lineItems={lineItems}
                      setLineItems={setLineItems}
                      onTotalsChange={handleTotalsChange}
                    />
                  </div>

                  {/* Submit Button */}
                  <div className="flex justify-start">
                    <Button 
                      type="submit" 
                      size="large"
                      disabled={isSubmitting}
                    >
                      {isSubmitting ? (
                        <>
                          <Spinner size="sm" fullScreen={false} className="size-5" />
                          Creating Invoice...
                        </>
                      ) : (
                        <>
                          <FilePlus size={20} />
                          Save Invoice
                        </>
                      )}
                    </Button>
                  </div>
                </div>

                {/* Sticky Invoice Summary - 1 column */}
                <div className="lg:col-span-1">
                  <div className="sticky top-6 p-6 bg-gradient-to-br from-primary/5 to-primary/10 border-2 border-primary/30 rounded-lg shadow-sm">
                    <h3 className="text-lg font-semibold text-foreground mb-4">Invoice Summary</h3>
                    <div className="space-y-3">
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-700">Subtotal (Before Tax):</span>
                        <span className="font-semibold">${formatCurrency(Number(invoice.total_dollar).toFixed(2))}</span>
                      </div>
                      {invoice.tax_rate > 0 && (
                        <div className="flex justify-between text-sm">
                          <span className="text-gray-700">Tax ({invoice.tax_rate}%):</span>
                          <span className="font-semibold">${formatCurrency(Number(taxAmount).toFixed(2))}</span>
                        </div>
                      )}
                      {discountAmount > 0 && (
                        <div className="flex justify-between text-sm">
                          <span className="text-gray-700">Discount:</span>
                          <span className="font-semibold text-green-600">-${formatCurrency(discountAmount.toFixed(2))}</span>
                        </div>
                      )}
                      <div className="pt-3 border-t-2 border-primary/30">
                        <div className="flex justify-between items-center">
                          <span className="text-base font-bold text-foreground">Total Amount:</span>
                          <span className="text-xl font-bold text-primary">
                            ${formatCurrency(Number(invoice.final_total).toFixed(2))}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </form>
          </div>
        </div>
      </div>
    </>
  );
};

export default CreateInvoicePage;